// Ordering & checkout: which channels the store takes, service times and
// auto-accept, dine-in mode, the ETA options the accept flow offers (moves
// return the list), and delivery areas with the point matcher checkout runs.

import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react';
import { api, ApiError, money } from '../../lib/api';
import { useBase } from '../../scope';
import { Err, MoneyInput } from './controls';
import { Check, errMsg, Head, int, intOrNull, SaveBtn, useAction } from './shared';
import {
  byRank, storeRequest, type DeliveryArea, type DeliveryAreaRequest, type DeliveryMatch, type DineInSettings,
  type EtaOption, type ServiceTimes, type SettingsBundle, type Store,
} from './types';

export function Ordering() {
  const base = useBase();
  const [bundle, setBundle] = useState<SettingsBundle | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<SettingsBundle>(`${base}/settings`).then(setBundle, (e) => setLoadErr(errMsg(e)));
  }, [base]);

  return (
    <>
      <Head title="Ordering & checkout" />
      <Err error={loadErr} />
      {!bundle && !loadErr && <div className="loading">Loading…</div>}
      {bundle && (
        <div className="set-cols">
          <div className="set-stack">
            <Channels base={base} />
            <ServiceTimesPanel base={base} initial={bundle.serviceTimes} />
            <DineInPanel base={base} initial={bundle.dineIn} />
            <EtaOptions base={base} initial={bundle.etaOptions} />
          </div>
          <div className="set-stack">
            <DeliveryAreas base={base} />
          </div>
        </div>
      )}
    </>
  );
}

// ── channels: the store's three switches, a full PUT of the store ───

function Channels(props: { base: string }) {
  const { base } = props;
  const [store, setStore] = useState<Store | null>(null);
  const act = useAction();
  const load = useCallback(async () => setStore(await api.get<Store>(base)), [base]);
  useEffect(() => { load().catch((e) => act.setError(errMsg(e))); }, [load, act.setError]);

  return (
    <div className="panel" data-section="channels">
      <h2>Channels</h2>
      {!store && <div className="loading">Loading…</div>}
      {store && (
        <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
          <div>
            <Check label="Pickup" checked={store.pickupEnabled} onChange={(v) => setStore({ ...store, pickupEnabled: v })} />
            <Check label="Delivery" checked={store.deliveryEnabled} onChange={(v) => setStore({ ...store, deliveryEnabled: v })} />
            <Check label="Dine-in" checked={store.dineInEnabled} onChange={(v) => setStore({ ...store, dineInEnabled: v })} />
          </div>
          <Err error={act.error} />
          <div>
            <SaveBtn busy={act.busy} saved={act.saved} label="Save channels"
              onClick={() => void act.run(async () => {
                // the rest of the profile is re-read so the PUT never replays a stale copy
                const fresh = await api.get<Store>(base);
                await api.put<Store>(base, storeRequest({ ...fresh, pickupEnabled: store.pickupEnabled, deliveryEnabled: store.deliveryEnabled, dineInEnabled: store.dineInEnabled }));
                await load();
              })} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── service times ──────────────────────────────────────────────────

function NumField(props: { label: string; value: number | null; onChange: (v: number | null) => void; nullable?: boolean; wide?: boolean }) {
  return (
    <div className="field narrow" style={props.wide ? { flexBasis: 170, minWidth: 170 } : undefined}>
      <label>{props.label}</label>
      <input className="input" type="number" min={0} aria-label={props.label} value={props.value ?? ''}
        onChange={(e) => props.onChange(props.nullable ? intOrNull(e.target.value) : int(e.target.value))} />
    </div>
  );
}

function ServiceTimesPanel(props: { base: string; initial: ServiceTimes }) {
  const { base } = props;
  const [st, setSt] = useState(props.initial);
  const act = useAction();
  return (
    <div className="panel" data-section="service-times">
      <h2>Service times</h2>
      <div className="set-stack" style={{ paddingTop: 8, gap: 14 }}>
        <div className="frow">
          <NumField label="Pickup lead (min)" value={st.pickupLeadMinutes} onChange={(v) => setSt({ ...st, pickupLeadMinutes: v ?? 0 })} />
          <NumField label="Delivery lead (min)" value={st.deliveryLeadMinutes} onChange={(v) => setSt({ ...st, deliveryLeadMinutes: v ?? 0 })} />
          <NumField label="Dine-in lead (min)" value={st.dineInLeadMinutes} onChange={(v) => setSt({ ...st, dineInLeadMinutes: v ?? 0 })} />
          <NumField label="Schedule ahead (days)" value={st.scheduleAheadDays} onChange={(v) => setSt({ ...st, scheduleAheadDays: v ?? 0 })} />
        </div>
        <div>
          <div className="overline" style={{ marginBottom: 4 }}>Auto-accept</div>
          <Check label="Auto-accept pickup orders" checked={st.autoAcceptPickup} onChange={(v) => setSt({ ...st, autoAcceptPickup: v })} />
          <Check label="Auto-accept delivery orders" checked={st.autoAcceptDelivery} onChange={(v) => setSt({ ...st, autoAcceptDelivery: v })} />
          <Check label="Auto-accept dine-in orders" checked={st.autoAcceptDineIn} onChange={(v) => setSt({ ...st, autoAcceptDineIn: v })} />
        </div>
        <div className="frow">
          <NumField label="Auto-accept ETA (min)" wide nullable value={st.autoAcceptEtaMinutes} onChange={(v) => setSt({ ...st, autoAcceptEtaMinutes: v })} />
          <div className="field"><span className="hint">Blank uses the lead time for the channel.</span></div>
        </div>
        <Err error={act.error} />
        <div>
          <SaveBtn busy={act.busy} saved={act.saved} label="Save service times"
            onClick={() => void act.run(async () => { setSt(await api.put<ServiceTimes>(`${base}/settings/service-times`, st)); })} />
        </div>
      </div>
    </div>
  );
}

// ── dine-in ────────────────────────────────────────────────────────

function DineInPanel(props: { base: string; initial: DineInSettings }) {
  const { base } = props;
  const [di, setDi] = useState(props.initial);
  const act = useAction();
  return (
    <div className="panel" data-section="dine-in">
      <h2>Dine-in</h2>
      <div className="set-stack" style={{ paddingTop: 8, gap: 14 }}>
        <div>
          <Check label="QR ordering at the table" checked={di.qrOrderingEnabled} onChange={(v) => setDi({ ...di, qrOrderingEnabled: v })} />
          <Check label="Require a table" checked={di.requireTableSelection} onChange={(v) => setDi({ ...di, requireTableSelection: v })} />
          <Check label="Ask for guest count" checked={di.promptGuestCount} onChange={(v) => setDi({ ...di, promptGuestCount: v })} />
          <Check label="Allow pay at the counter" checked={di.payAtStoreAllowed} onChange={(v) => setDi({ ...di, payAtStoreAllowed: v })} />
        </div>
        <Err error={act.error} />
        <div>
          <SaveBtn busy={act.busy} saved={act.saved} label="Save dine-in"
            onClick={() => void act.run(async () => { setDi(await api.put<DineInSettings>(`${base}/settings/dine-in`, di)); })} />
        </div>
      </div>
    </div>
  );
}

// ── ETA options ────────────────────────────────────────────────────

function EtaOptions(props: { base: string; initial: EtaOption[] }) {
  const { base } = props;
  const [etas, setEtas] = useState<EtaOption[]>([...props.initial].sort(byRank));
  const [minutes, setMinutes] = useState('');
  const [label, setLabel] = useState('');
  const [editing, setEditing] = useState<{ id: string; minutes: string; label: string } | null>(null);
  const act = useAction();

  const load = async () => setEtas((await api.get<EtaOption[]>(`${base}/eta-options`)).sort(byRank));

  const add = () =>
    void act.run(async () => {
      await api.post<EtaOption>(`${base}/eta-options`, { minutes: int(minutes), label: label.trim() || null });
      setMinutes('');
      setLabel('');
      await load();
    });
  const saveEdit = () => {
    if (!editing) return;
    void act.run(async () => {
      await api.put<EtaOption>(`${base}/eta-options/${editing.id}`, { minutes: int(editing.minutes), label: editing.label.trim() || null });
      setEditing(null);
      await load();
    });
  };
  const move = (e: EtaOption, dir: 'up' | 'down') =>
    void act.run(async () => {
      setEtas((await api.post<EtaOption[]>(`${base}/eta-options/${e.etaOptionId}/move-${dir}`)).sort(byRank));
    });
  const remove = (e: EtaOption) => {
    if (!confirm(`Delete the ${e.minutes}-minute option?`)) return;
    void act.run(async () => {
      await api.del(`${base}/eta-options/${e.etaOptionId}`);
      await load();
    });
  };

  return (
    <div className="panel" data-section="eta-options">
      <h2>ETA options</h2>
      <div className="hint" style={{ margin: '6px 0 4px' }}>The ready-in choices staff pick from when accepting an order.</div>
      {etas.length === 0 && <div className="loading">No ETA options yet — accepting asks for minutes.</div>}
      {etas.map((e, i) =>
        editing?.id === e.etaOptionId ? (
          <div key={e.etaOptionId} className="rowline">
            <input className="input" type="number" min={1} style={{ maxWidth: 84 }} aria-label="Edit minutes" value={editing.minutes}
              onChange={(ev) => setEditing({ ...editing, minutes: ev.target.value })} />
            <div className="grow">
              <input className="input" aria-label="Edit label" placeholder="label" value={editing.label}
                onChange={(ev) => setEditing({ ...editing, label: ev.target.value })} />
            </div>
            <button className="btn btn-primary btn-sm" disabled={act.busy || intOrNull(editing.minutes) === null} onClick={saveEdit}>Save</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        ) : (
          <div key={e.etaOptionId} className="rowline" data-minutes={e.minutes}>
            <div className="grow">
              <b>{e.minutes} min</b>
              {e.label && <span className="sub">{e.label}</span>}
            </div>
            <button className="btn btn-ghost btn-sm" title="Move up" disabled={act.busy || i === 0} onClick={() => move(e, 'up')}><ArrowUp size={14} /></button>
            <button className="btn btn-ghost btn-sm" title="Move down" disabled={act.busy || i === etas.length - 1} onClick={() => move(e, 'down')}><ArrowDown size={14} /></button>
            <button className="btn btn-ghost btn-sm" title="Edit" aria-label={`Edit ${e.minutes} min`}
              onClick={() => setEditing({ id: e.etaOptionId, minutes: String(e.minutes), label: e.label ?? '' })}><Pencil size={14} /></button>
            <button className="btn btn-ghost btn-sm" title="Delete" aria-label={`Delete ${e.minutes} min`} disabled={act.busy} onClick={() => remove(e)}><Trash2 size={14} /></button>
          </div>
        ),
      )}
      <div className="frow" style={{ marginTop: 12 }}>
        <div className="field narrow">
          <label>Minutes</label>
          <input className="input" type="number" min={1} aria-label="New ETA minutes" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        </div>
        <div className="field">
          <label>Label (optional)</label>
          <input className="input" aria-label="New ETA label" placeholder="About half an hour" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <button className="btn btn-outline" disabled={act.busy || intOrNull(minutes) === null} onClick={add}>Add ETA</button>
      </div>
      <Err error={act.error} />
    </div>
  );
}

// ── delivery areas + the matcher ───────────────────────────────────

type AreaDraft = DeliveryAreaRequest & { id: string | null };
const freshArea = (): AreaDraft => ({ id: null, name: '', polygonWkt: '', deliveryChargeInCents: 0, minOrderInCents: null, applyThirdPartyFee: false, active: true });

type MatchOutcome = { matched: true; match: DeliveryMatch } | { matched: false; message: string };

function DeliveryAreas(props: { base: string }) {
  const { base } = props;
  const [areas, setAreas] = useState<DeliveryArea[] | null>(null);
  const [draft, setDraft] = useState<AreaDraft | null>(null);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [outcome, setOutcome] = useState<MatchOutcome | null>(null);
  const act = useAction();
  const matchAct = useAction();

  const load = useCallback(async () => setAreas((await api.get<DeliveryArea[]>(`${base}/delivery-areas`)).sort(byRank)), [base]);
  useEffect(() => { load().catch((e) => act.setError(errMsg(e))); }, [load, act.setError]);

  const save = () => {
    if (!draft) return;
    const { id, ...rest } = draft;
    const body: DeliveryAreaRequest = { ...rest, name: rest.name.trim(), polygonWkt: rest.polygonWkt.trim() };
    void act.run(async () => {
      if (id) await api.put<DeliveryArea>(`${base}/delivery-areas/${id}`, body);
      else await api.post<DeliveryArea>(`${base}/delivery-areas`, body);
      setDraft(null);
      await load();
    });
  };
  const remove = (a: DeliveryArea) => {
    if (!confirm(`Delete the delivery area “${a.name}”?`)) return;
    void act.run(async () => {
      await api.del(`${base}/delivery-areas/${a.areaId}`);
      setDraft((d) => (d?.id === a.areaId ? null : d));
      await load();
    });
  };

  // outside_delivery_area is an answer, not a failure: it renders as the outcome
  const test = () =>
    void matchAct.run(async () => {
      setOutcome(null);
      try {
        setOutcome({ matched: true, match: await api.post<DeliveryMatch>(`${base}/delivery-areas/match`, { lat: Number(lat), lng: Number(lng) }) });
      } catch (e) {
        if (e instanceof ApiError && e.code === 'outside_delivery_area') setOutcome({ matched: false, message: e.message });
        else throw e;
      }
    });

  return (
    <>
      <div className="panel" data-section="delivery-areas">
        <h2>Delivery areas</h2>
        {areas === null && <div className="loading">Loading…</div>}
        {areas?.length === 0 && <div className="loading">No delivery areas yet.</div>}
        {areas?.map((a) => (
          <div key={a.areaId} className="rowline" data-area-id={a.areaId}>
            <div className="grow">
              <b>{a.name}{a.active ? '' : ' (inactive)'}</b>
              <span className="sub">
                {money(a.deliveryChargeInCents)} delivery{a.minOrderInCents !== null ? ` · min order ${money(a.minOrderInCents)}` : ''}
                {a.applyThirdPartyFee ? ' · courier fee passed on' : ''}
              </span>
            </div>
            <button className="btn btn-ghost btn-sm" title={`Edit ${a.name}`} aria-label={`Edit ${a.name}`}
              onClick={() => setDraft({ id: a.areaId, name: a.name, polygonWkt: a.polygonWkt, deliveryChargeInCents: a.deliveryChargeInCents, minOrderInCents: a.minOrderInCents, applyThirdPartyFee: a.applyThirdPartyFee, active: a.active })}>
              <Pencil size={14} />
            </button>
            <button className="btn btn-ghost btn-sm" title={`Delete ${a.name}`} aria-label={`Delete ${a.name}`} disabled={act.busy} onClick={() => remove(a)}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {!draft && (
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-outline" onClick={() => setDraft(freshArea())}>New delivery area</button>
          </div>
        )}
        {draft && (
          <div className="set-stack" style={{ marginTop: 14, gap: 12 }}>
            <div className="overline">{draft.id ? 'Edit area' : 'New area'}</div>
            <div className="frow">
              <div className="field">
                <label>Name</label>
                <input className="input" aria-label="Area name" placeholder="Zone 1" value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div className="field narrow">
                <label>Delivery fee ($)</label>
                <MoneyInput title="Delivery fee" value={draft.deliveryChargeInCents} onChange={(c) => setDraft({ ...draft, deliveryChargeInCents: c ?? 0 })} />
              </div>
              <div className="field narrow">
                <label>Min order ($)</label>
                <MoneyInput title="Minimum order" placeholder="" value={draft.minOrderInCents} onChange={(c) => setDraft({ ...draft, minOrderInCents: c })} />
              </div>
            </div>
            <div className="field">
              <label>Boundary (WKT polygon, longitude latitude pairs, closed)</label>
              <textarea className="input mono-area" aria-label="Area polygon" value={draft.polygonWkt}
                placeholder="POLYGON((151.20 -33.89, 151.22 -33.89, 151.22 -33.87, 151.20 -33.87, 151.20 -33.89))"
                onChange={(e) => setDraft({ ...draft, polygonWkt: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <Check label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
              <Check label="Pass the courier fee on" checked={draft.applyThirdPartyFee} onChange={(v) => setDraft({ ...draft, applyThirdPartyFee: v })} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <SaveBtn busy={act.busy} saved={false} onClick={save} disabled={!draft.name.trim() || !draft.polygonWkt.trim()}
                label={draft.id ? 'Save area' : 'Create area'} />
              <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </div>
        )}
        <Err error={act.error} />
      </div>

      <div className="panel" data-section="match">
        <h2>Test a point</h2>
        <div className="hint" style={{ margin: '8px 0 12px' }}>
          Which active area covers a coordinate — the same match checkout runs.
        </div>
        <div className="frow">
          <div className="field narrow" style={{ flexBasis: 140, minWidth: 140 }}>
            <label>Latitude</label>
            <input className="input" type="number" step="any" aria-label="Latitude" placeholder="-33.88" value={lat} onChange={(e) => setLat(e.target.value)} />
          </div>
          <div className="field narrow" style={{ flexBasis: 140, minWidth: 140 }}>
            <label>Longitude</label>
            <input className="input" type="number" step="any" aria-label="Longitude" placeholder="151.21" value={lng} onChange={(e) => setLng(e.target.value)} />
          </div>
          <button className="btn btn-outline" disabled={matchAct.busy || lat.trim() === '' || lng.trim() === ''} onClick={test}>
            {matchAct.busy ? 'Matching…' : 'Test point'}
          </button>
        </div>
        <Err error={matchAct.error} />
        {outcome && (
          <div className="kv" style={{ marginTop: 12 }} data-matched={outcome.matched}>
            {outcome.matched ? (
              <>Delivers: <b>{outcome.match.name}</b> — {money(outcome.match.deliveryChargeInCents)} delivery
                {outcome.match.minOrderInCents !== null ? <>, min order {money(outcome.match.minOrderInCents)}</> : null}.</>
            ) : (
              <>No delivery: {outcome.message}</>
            )}
          </div>
        )}
      </div>
    </>
  );
}
